import { QuestionType } from '@webapp/common/resources/survey';

import { ConjointApi } from '@webapp/survey/src/api/types/conjoint';
import type { QuestionActor, QuestionActorRef, QuestionContext } from '@webapp/survey/src/machine/question-machine';

import type { ResponseEventParams } from '../entities/response-machine';

import { AbTestingApi } from './types/ab-testing';
import { AgreementApi } from './types/agreement';
import { CardSortingApi } from './types/card-sorting';
import { VoidQuestionApi } from './types/common/void';
import { CsiApi } from './types/csi';
import { DatetimeApi } from './types/datetime';
import { DistributeScaleApi } from './types/distribute-scale';
import { DropdownListApi } from './types/dropdown-list';
import { EmailApi } from './types/email';
import { FewOfListApi } from './types/few-of-list';
import { FigmaTestApi } from './types/figma-test';
import { FileUploadApi } from './types/file-upload';
import { FirstClickApi } from './types/first-click';
import { FiveSecTestApi } from './types/five-sec-test';
import { GroupFreeAnswersApi } from './types/group-free-answers';
import { KanoApi } from './types/kano';
import { LongTextApi } from './types/long-text';
import { MatrixDropdownListApi } from './types/matrix-dropdown-list';
import { MatrixFewAnswersApi } from './types/matrix-few-answers';
import { MatrixRatingApi } from './types/matrix-rating';
import { MatrixSingleAnswerApi } from './types/matrix-single-answer';
import { MatrixTextAnswerApi } from './types/matrix-text-answer';
import { MaxDiffApi } from './types/max-diff';
import { NameApi } from './types/name';
import { NestedListApi } from './types/nested-list';
import { NpsApi } from './types/nps';
import { NumberApi } from './types/number';
import { OneOfListApi } from './types/one-of-list';
import { PairComparisonApi } from './types/pair-comparison';
import { PhoneApi } from './types/phone';
import { RankApi } from './types/rank';
import { ResidenceApi } from './types/residence';
import { ScaleApi } from './types/scale';
import { SelectFewImageApi } from './types/select-few-image';
import { SelectOneImageApi } from './types/select-one-image';
import { SemDiffApi } from './types/sem-diff';
import { ShortTextApi } from './types/short-text';
import { SmileApi } from './types/smile';
import { StarApi } from './types/star';
import { TextPerceptionApi } from './types/text-perception';
import { VideoTestApi } from './types/video-test';

export interface AnswersRequest {
    question: number;
    data: Array<{
        answer?: number;
        group?: number;
        int?: number;
        text?: string;
        longtext?: string;
        bool?: boolean;
        comment?: string;
        expired?: boolean;
        cantRate?: boolean;
        json?: any;
    }>;
    comment: string;
}

export interface QuestionApi {
    query: (q: QuestionActorRef) => AnswersRequest;
    validate: (q: QuestionContext) => boolean;
    change: (q: QuestionContext, evt: ResponseEventParams) => void;
    changeExtra: (q: QuestionActor, v: PrimitiveValue) => void;
}

export const TYPES_FILE_MAP: Record<Exclude<QuestionType, QuestionType.DEFAULT>, QuestionApi> = {
    [QuestionType.ONE_OF_LIST]: new OneOfListApi(),
    [QuestionType.FEW_OF_LIST]: new FewOfListApi(),
    [QuestionType.TEST_ONE_OF_LIST]: new OneOfListApi(),
    [QuestionType.TEST_FEW_OF_LIST]: new FewOfListApi(),
    [QuestionType.SELECT_ONE_IMAGE]: new SelectOneImageApi(),
    [QuestionType.SELECT_FEW_IMAGE]: new SelectFewImageApi(),
    [QuestionType.DROPDOWN_LIST]: new DropdownListApi(),
    [QuestionType.STAR]: new StarApi(),
    [QuestionType.NUMBER]: new NumberApi(),
    [QuestionType.SHORT_TEXT]: new ShortTextApi(),
    [QuestionType.TEST_TEXT]: new ShortTextApi(),
    [QuestionType.LONG_TEXT]: new LongTextApi(),
    [QuestionType.GROUP_FREE_ANSWERS]: new GroupFreeAnswersApi(),
    [QuestionType.EMAIL]: new EmailApi(),
    [QuestionType.AGREEMENT]: new AgreementApi(),
    [QuestionType.NPS]: new NpsApi(),
    [QuestionType.RANK]: new RankApi(),
    [QuestionType.TEST_RANK]: new RankApi(),
    [QuestionType.SCALE]: new ScaleApi(),
    [QuestionType.SEM_DIFF]: new SemDiffApi(),
    [QuestionType.DISTRIBUTE_SCALE]: new DistributeScaleApi(),
    [QuestionType.DATETIME]: new DatetimeApi(),
    [QuestionType.PHONE]: new PhoneApi(),
    [QuestionType.SMILE]: new SmileApi(),
    [QuestionType.TEXT_BLOCK]: new VoidQuestionApi(),
    [QuestionType.RESIDENCE]: new ResidenceApi(),
    [QuestionType.FILE_UPLOAD]: new FileUploadApi(),
    [QuestionType.NAME]: new NameApi(),
    [QuestionType.MATRIX_FEW_ANSWERS]: new MatrixFewAnswersApi(),
    [QuestionType.MATRIX_SINGLE_ANSWER]: new MatrixSingleAnswerApi(),
    [QuestionType.MATRIX_TEXT_ANSWER]: new MatrixTextAnswerApi(),
    [QuestionType.MATRIX_RATING]: new MatrixRatingApi(),
    [QuestionType.MATRIX_DROPDOWN_LIST]: new MatrixDropdownListApi(),
    [QuestionType.NESTED_LIST]: new NestedListApi(),
    [QuestionType.CSI]: new CsiApi(),
    [QuestionType.KANO]: new KanoApi(),
    [QuestionType.AB_TESTING]: new AbTestingApi(),
    [QuestionType.TEXT_PERCEPTION]: new TextPerceptionApi(),
    [QuestionType.PAIR_COMPARISON]: new PairComparisonApi(),
    [QuestionType.FIRST_CLICK]: new FirstClickApi(),
    [QuestionType.CARD_SORTING]: new CardSortingApi(),
    [QuestionType.MAX_DIFF]: new MaxDiffApi(),
    [QuestionType.VIDEO_TEST]: new VideoTestApi(),
    [QuestionType.FIVE_SEC_TEST]: new FiveSecTestApi(),
    [QuestionType.FIGMA_TEST]: new FigmaTestApi(),
    [QuestionType.CONJOINT]: new ConjointApi(),
};

export const getApiClass = (t: Exclude<QuestionType, QuestionType.DEFAULT>): QuestionApi => {
    const ac = TYPES_FILE_MAP[t];
    if (!ac?.validate) {
        console.error('type not found', t, ac);
        return new VoidQuestionApi();
    }

    return ac;
};
